/**
 * AuthorSelectButton — `.author-select` from the prototype
 * (cms-redesign-mockup.html ~4340–4361). Replaces the v1 MUI
 * Autocomplete with a custom button+dropdown so the resting state
 * actually shows the selected author's avatar + name + role inline,
 * matching the prototype's edit-page look.
 *
 *   <AuthorSelectButton
 *     value={formData.session_author_id}
 *     onChange={(id) => setField('session_author_id', id)}
 *   />
 *
 * Wires:
 *   • Reads `state.authors.authors` (same slice the v1 AuthorSelector
 *     uses), and fires `fetchAuthors()` once on mount if the slice is
 *     empty.
 *   • Value is the integer `author_id` (or 0 / null for "no author").
 *
 * Behavior:
 *   • Click the button → dropdown opens beneath. Closes on
 *     outside-click and on Escape.
 *   • Search input filters by author_name + author_title (case-
 *     insensitive substring match). Focus moves to the input on open.
 *   • Currently-selected author gets the `.parent-option.active`
 *     teal-soft highlight. Click any row → onChange(author_id) and
 *     close.
 *   • Initials fallback when an author has no image — colored disk
 *     with their first/last initials (matches the V2 Author cell
 *     pattern used elsewhere).
 *
 * Visual recipe:
 *   • Resting button: cream bg + sand border, 38×38 circular avatar,
 *     navy name (14/700), muted role (11.5)
 *   • Hover: teal-green border + surface bg
 *   • Dropdown: surface card, 8/24 shadow, search header on cream,
 *     scrollable list (max 320px) with teal-soft active state
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthors } from '../../../actions/authorsActions';
import { cn } from '../cn';

/* ─── Helpers ──────────────────────────────────────────────────────── */

function initials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// Cycle the avatar tint deterministically off the author_id so the
// same author always gets the same color across the app. Tints
// match the V2 design palette.
const AVATAR_TINTS = ['rgb(var(--color-teal-green))', '#C4715B', '#9DA67E', '#5C7E72', 'rgb(var(--color-coral))'];
function tintForAuthor(authorId, hasImage) {
  if (hasImage) return undefined;
  const idx = Math.abs(Number(authorId) || 0) % AVATAR_TINTS.length;
  return AVATAR_TINTS[idx];
}

/* ─── Avatar (small) ───────────────────────────────────────────────── */

function Avatar({ name, image, id, size = 38 }) {
  const tint = tintForAuthor(id, !!image);
  return (
    <span
      aria-hidden
      className="grid place-items-center text-white font-condensed font-bold"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        fontSize: size >= 32 ? 13 : 10,
        flexShrink: 0,
        background: tint || 'rgb(var(--color-cream))',
        backgroundImage: image ? `url('${image}')` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: 'inset 0 0 0 1px #00000010',
      }}
    >
      {!image && initials(name)}
    </span>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function AuthorSelectButton({
  value,
  onChange,
  className,
}) {
  const dispatch = useDispatch();
  const authors = useSelector((s) => s.authors.authors);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!authors) dispatch(fetchAuthors());
  }, [dispatch, authors]);

  // Focus the search input the moment the dropdown opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      // Defer so the input exists in the DOM before we focus.
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Click-outside + Escape close the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = useMemo(() => {
    if (!Array.isArray(authors) || !value) return null;
    return authors.find((a) => Number(a.author_id) === Number(value)) || null;
  }, [authors, value]);

  const filtered = useMemo(() => {
    if (!Array.isArray(authors)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return authors;
    return authors.filter(
      (a) =>
        (a.author_name || '').toLowerCase().includes(q) ||
        (a.author_title || '').toLowerCase().includes(q),
    );
  }, [authors, query]);

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center cursor-pointer w-full',
          'transition-all duration-150',
        )}
        style={{
          gap: 12,
          padding: '6px 14px 6px 6px',
          background: 'rgb(var(--color-cream))', /* cream */
          border: '1px solid rgb(var(--color-sand))', /* sand */
          borderRadius: 8,
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
          e.currentTarget.style.background = 'rgb(var(--color-surface))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
          e.currentTarget.style.background = 'rgb(var(--color-cream))';
        }}
      >
        {selected ? (
          <Avatar
            name={selected.author_name}
            image={selected.author_image}
            id={selected.author_id}
          />
        ) : (
          // Empty-state avatar: cream disk with a faint dashed border
          // so the slot still looks complete pre-selection.
          <span
            aria-hidden
            className="grid place-items-center text-text-muted font-condensed font-bold"
            style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              fontSize: 13,
              flexShrink: 0,
              background: 'rgb(var(--color-cream))',
              border: '1px dashed #C8BFA8',
            }}
          >
            ?
          </span>
        )}
        <span className="flex-1 min-w-0" style={{ textAlign: 'left' }}>
          {selected ? (
            <>
              <span
                className="text-navy block truncate"
                style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}
              >
                {selected.author_name}
              </span>
              {selected.author_title && (
                <span
                  className="text-text-muted block truncate"
                  style={{ fontSize: 11.5, marginTop: 2 }}
                >
                  {selected.author_title}
                </span>
              )}
            </>
          ) : (
            <span className="text-text-muted italic" style={{ fontSize: 13 }}>
              Pick an author…
            </span>
          )}
        </span>
        <span className="text-text-muted" style={{ fontSize: 11, flexShrink: 0 }}>
          ▾
        </span>
      </button>

      {open && (
        <div
          className="absolute"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'rgb(var(--color-surface))', /* surface */
            border: '1px solid rgb(var(--color-sand))', /* sand */
            borderRadius: 8,
            boxShadow: '0 8px 24px #2C3E5018',
            overflow: 'hidden',
            zIndex: 30,
          }}
        >
          {/* Search header */}
          <div
            className="flex items-center"
            style={{
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid #EFE7D8',
              background: 'rgb(var(--color-cream))',
            }}
          >
            <span aria-hidden className="text-text-muted" style={{ fontSize: 13, flexShrink: 0 }}>
              ⌕
            </span>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or title…"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: 'rgb(var(--color-navy))',
              }}
            />
          </div>

          {/* Options list */}
          <div
            style={{
              maxHeight: 320,
              overflowY: 'auto',
              padding: '6px 0',
            }}
          >
            {!authors ? (
              <div
                className="text-text-muted italic"
                style={{ padding: '12px 14px', fontSize: 13 }}
              >
                Loading authors…
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="text-text-muted italic"
                style={{ padding: '12px 14px', fontSize: 13 }}
              >
                No authors match "{query}".
              </div>
            ) : (
              filtered.map((a) => {
                const isActive = Number(a.author_id) === Number(value);
                return (
                  <button
                    key={a.author_id}
                    type="button"
                    onClick={() => {
                      onChange?.(Number(a.author_id));
                      setOpen(false);
                    }}
                    className="w-full flex items-center cursor-pointer"
                    style={{
                      gap: 10,
                      padding: '8px 14px',
                      fontSize: 13,
                      color: isActive ? '#5C7E72' : 'rgb(var(--color-navy))',
                      fontWeight: isActive ? 600 : 400,
                      background: isActive ? '#75A29333' : 'transparent',
                      border: 0,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 120ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = 'rgb(var(--color-cream))';
                      e.currentTarget.style.color = 'rgb(var(--color-navy))';
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Avatar
                      name={a.author_name}
                      image={a.author_image}
                      id={a.author_id}
                      size={28}
                    />
                    <span className="flex-1 min-w-0" style={{ textAlign: 'left' }}>
                      <span className="block truncate" style={{ fontWeight: 600 }}>
                        {a.author_name}
                      </span>
                      {a.author_title && (
                        <span
                          className="text-text-muted block truncate"
                          style={{ fontSize: 11, marginTop: 1, fontWeight: 400 }}
                        >
                          {a.author_title}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
